import React, { useEffect, useState } from 'react'
import { flow, get, toUpper } from 'lodash/fp'

import { Primary, SelectField, Text } from '@cinch-labs/design-system'
import Link from '@cinch/oxd-components/link/src'

import { getMake as getMakeLabel, getModel as getModelLabel } from '@cinch/oxd-utils/manufacturers/src'

import * as styles from './vehicles-filter.module.css'

interface Make {
  make: string
  models: string[]
}

interface SelectFieldOption {
  label: string
  value: string
}

export interface VehiclesFilterProps {
  heading: string
  makesAndModels: Make[]
  getFilters: () => void
  updateFiltersForm: (make: string, model: string) => void
}

export const getMakeOption = (currentMake: Make, index: number): SelectFieldOption => ({
  label: getMakeLabel(currentMake.make),
  value: String(index),
})

export const getModelOption = (selectedMake: string) => (currentModel: string, index: number): SelectFieldOption => ({
  label: getModelLabel(selectedMake, currentModel),
  value: String(index),
})

const getParameterValue = flow(get('label'), toUpper)

const VehiclesFilter: React.FC<VehiclesFilterProps> = ({ heading, makesAndModels, getFilters, updateFiltersForm }) => {
  const [selectedMake, setSelectedMake] = useState('')
  const [selectedModel, setSelectedModel] = useState('')

  useEffect(() => {
    getFilters()
  }, [])

  const makeOptions: SelectFieldOption[] = makesAndModels.map(getMakeOption)
  let modelOptions: SelectFieldOption[] = []

  if (selectedMake) {
    const { make, models } = makesAndModels[parseInt(selectedMake)]
    modelOptions = models.map(getModelOption(make))
  }

  const handleChange = (setValue: (value: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.currentTarget
    value === 'Any' ? setValue('') : setValue(value)
  }

  const handleClick = () => {
    let make = ''
    let model = ''

    if (selectedMake) {
      make = getParameterValue(makeOptions[parseInt(selectedMake)])
    }

    if (selectedModel) {
      model = getParameterValue(modelOptions[parseInt(selectedModel)])
    }

    updateFiltersForm(make, model)
  }

  return (
    <section className={styles.container}>
      <div className={styles.headingContainer}>
        <Text fontWeight={'bold'}>{heading}</Text>
      </div>

      <SelectField
        id="make"
        data-testid="make-select"
        options={makeOptions}
        placeholder={'Any make'}
        placeholderValue={'Any'}
        value={selectedMake}
        onChange={handleChange(setSelectedMake)}
      />

      <SelectField
        id="model"
        data-testid="model-select"
        options={modelOptions}
        placeholder={'Any model'}
        placeholderValue={'Any'}
        value={selectedModel}
        onChange={handleChange(setSelectedModel)}
        disabled={!selectedMake}
      />

      <div className={styles.buttonContainer}>
        <Primary onClick={handleClick} isFullWidth={true}>
          Show all cars
        </Primary>
      </div>

      <div className={styles.linkContainer}>
        <Link className={styles.link} to={'/lifestyle/budget'}>
          Help me choose
        </Link>
      </div>

      <div className={styles.linkContainer}>
        <Link className={styles.link} to={'/dealers/find-vehicle'}>
          Show dealer cars
        </Link>
      </div>
    </section>
  )
}

export default VehiclesFilter
